'use client';

import { Tag, TagVariant } from '@industriousoffice/react-components';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { useLocale, useTranslations } from 'next-intl';
import { useSearchParams } from 'next/navigation';

import useSearchPageParams from '@/components/search/useSearchPageParams';
import { useDateUtils } from '@/hooks/useDateUtils';
import { GeolocalizationDto, LocationPhases } from '@/lib/locations';
import { Frequency } from '@/models/Frequency.enum';
import { Products } from '@/models/Products.enum';
import {
  getOpeningDate,
  getOpeningLabelTranslationKey,
  shouldShowOpeningLabel,
} from '@/utils/locations';

import { OnDemandProduct, getProductFromSearchParams } from './utils';

type ProductRadioGroupProps = {
  currencyIsoCode: string;
  products: OnDemandProduct[];
  locationFirstDayOfBusiness: string | null;
  locationGeolocalization: GeolocalizationDto;
  hideOpeningDate: boolean;
  locationPhase: LocationPhases;
};

export default function ProductRadioGroup({
  currencyIsoCode,
  products,
  locationFirstDayOfBusiness,
  locationGeolocalization,
  hideOpeningDate,
  locationPhase,
}: ProductRadioGroupProps) {
  const searchParams = useSearchParams();
  const { updateQuery } = useSearchPageParams();

  const selection: Products = getProductFromSearchParams(
    searchParams,
    products
  );

  const updateSelectedProduct = (product: string) => {
    const params = new URLSearchParams(searchParams.toString());
    params.set('product', product);
    window.history.replaceState(null, '', `?${params.toString()}`);
    updateQuery({
      seats: undefined,
      amenities: undefined,
      day: undefined,
      startTime: undefined,
      endTime: undefined,
    });
  };

  return (
    <RadioGroupPrimitive.Root
      className="grid grid-cols-3 gap-2 mb-5 md:gap-5"
      value={selection}
      onValueChange={updateSelectedProduct}
      name="product-type"
      aria-label="Workspaces Available"
    >
      {products.map((product) => (
        <ProductRadioButton
          key={product.value}
          currencyIsoCode={currencyIsoCode}
          title={product.title}
          prices={product.prices ?? []}
          tag={product.tag}
          value={product.value}
          locationFirstDayOfBusiness={locationFirstDayOfBusiness}
          locationGeolocalization={locationGeolocalization}
          hideOpeningDate={hideOpeningDate}
          locationPhase={locationPhase}
        />
      ))}
    </RadioGroupPrimitive.Root>
  );
}

type ProductRadioButtonProps = {
  currencyIsoCode: string;
  title: string;
  prices: { price: number; frequency: Frequency }[];
  tag: string;
  value: Products.Office | Products.MeetingRoom | Products.Coworking;
  locationFirstDayOfBusiness: string | null;
  locationGeolocalization: GeolocalizationDto;
  hideOpeningDate: boolean;
  locationPhase: LocationPhases;
};

function ProductRadioButton({
  currencyIsoCode,
  title,
  prices,
  tag,
  value,
  locationFirstDayOfBusiness,
  locationGeolocalization,
  hideOpeningDate,
  locationPhase,
}: ProductRadioButtonProps) {
  const t = useTranslations('general');
  const locale = useLocale();
  const { format, addDays } = useDateUtils();

  const tomorrowDate = format(addDays(new Date(), 1), 'P');

  const openingDate = locationFirstDayOfBusiness
    ? getOpeningDate(locationFirstDayOfBusiness)
    : null;

  const showOpeningLabel = shouldShowOpeningLabel({
    hideOpeningDate: hideOpeningDate,
    firstDayOfBusinessISO: locationFirstDayOfBusiness,
    phase: locationPhase,
  });

  const startingAtValue = prices
    .map(({ price, frequency }) =>
      t(getFrequencyTranslationKey(frequency), {
        price: Intl.NumberFormat(locale, {
          style: 'currency',
          currency: currencyIsoCode,
          maximumFractionDigits: 0,
        }).format(price),
      })
    )
    .join(' • ');

  return (
    <RadioGroupPrimitive.Item
      className={`
        w-full flex flex-col  gap-2 items-center max-lg:px-1 max-lg:py-[10px] border rounded-lg cursor-pointer 
        lg:text-start lg:flex-row lg:gap-5 lg:p-5 
        data-[state=checked]:outline data-[state=checked]:outline-teal-40 data-[state=checked]:outline-[3px] data-[state=checked]:outline-offset-[-3px] 
        border-grey-90 box-border
        hover:border-grey-60
      `}
      value={value}
      data-testid={`${value}-button`}
    >
      <div className="relative flex flex-col w-full gap-2">
        <div className="max-lg:hidden absolute self-end w-[23px] h-[23px] bg-white border border-solid rounded-full cursor-pointer border-grey-50">
          <RadioGroupPrimitive.Indicator className="absolute inset-0 flex items-center justify-center leading-0">
            <div
              className="w-[15px] h-[15px] rounded-full bg-ocean-60"
              data-testid="product-selected"
            />
          </RadioGroupPrimitive.Indicator>
        </div>
        <h4
          className="font-bold lg:mr-5 lg:text-xl"
          data-testid={`${value}-title`}
        >
          {title}
        </h4>
        <Tag
          className="max-lg:hidden bg-[#E4EDEC80] text-grey-05 text-opacity-80"
          variant={TagVariant.None}
          text={tag}
          testId={`${value}-tag`}
        />
        {prices.length > 0 && value !== Products.Office && (
          <p
            data-testid={`${value}-price`}
            className="max-lg:hidden text-grey-25"
          >
            {`${t('available-from')} `}
            <span>{startingAtValue}</span>
          </p>
        )}
        {value === Products.Office && (
          <p
            data-testid={`${value}-price`}
            className="max-lg:hidden text-grey-25"
          >
            {showOpeningLabel && openingDate
              ? t(
                  `opening-${getOpeningLabelTranslationKey({
                    openingDate,
                    latitude: locationGeolocalization.lat,
                  })}`,
                  {
                    date: openingDate,
                    year: openingDate.getFullYear(),
                  }
                )
              : t('move-in-ready', { date: tomorrowDate })}
          </p>
        )}
      </div>
    </RadioGroupPrimitive.Item>
  );
}

function getFrequencyTranslationKey(frequency: Frequency) {
  switch (frequency) {
    case Frequency.Daily:
      return 'per-day';
    case Frequency.Hourly:
      return 'per-hour';
    case Frequency.Monthly:
      return 'per-month';
  }
}
